import React, { useEffect, useState } from "react";
import { getUserById } from "../services/api";
import { FaArrowLeft, FaUser, FaVial, FaHistory, FaFilePdf, FaExclamationTriangle, FaCoffee } from "react-icons/fa";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default function UserDetails({ id, onBack }) {
  const [user, setUser] = useState(null);

  useEffect(() => {
    load();
  }, [id]);

  const load = async () => {
    const data = await getUserById(id);
    setUser(data);
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const generatePDF = () => {
    const doc = new jsPDF();
    const d = user.data;
    const pageWidth = doc.internal.pageSize.getWidth();
    let currentY = 15;

    // --- Header Section ---
    doc.setFontSize(14);
    doc.setFont(undefined, 'bold');
    doc.text("Dr. Dinesh Zirpe Liver Health Assessment", 14, currentY);
    currentY += 7;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    doc.text("Awareness Screening Support Structured Data Collection", 14, currentY);
    currentY += 4;
    doc.line(14, currentY, pageWidth - 14, currentY);
    currentY += 10;

    // --- Section 1: Summary ---
    doc.setFont(undefined, 'bold');
    doc.text("Section 1: Summary", 14, currentY);
    
    autoTable(doc, {
      startY: currentY + 2,
      head: [['Metric', 'Value']],
      body: [
        ['Risk Level', d?.Risk_Level || "Low current risk"],
        ['Normalized Score', `${d?.Normalized_Score || 0}/30`],
        ['Raw Score', `${d?.Raw_Score || 0}/46`],
        ['Fatty Liver Probability', `${d?.Fatty_Liver_Probability || 0}%`],
        ['Fibrosis Concern Probability', `${d?.Fibrosis_Concern_Probability || 0}%`],
        ['BMI', `${d?.BMI || 0} (${d?.BMI_Category || "Normal"})`],
        ['Waist', `${d?.Waist_cm || 0} cm (${d?.Waist_Risk || "Normal"})`],
        ['Ultrasound Done', d?.USG_Fatty_Liver || "No"]
      ],
      theme: 'grid',
      headStyles: { fillColor: [41, 128, 185], cellPadding: 2 },
      bodyStyles: { cellPadding: 2 },
      styles: { fontSize: 9 }
    });

    currentY = doc.lastAutoTable.finalY + 8;

    // --- Section 2: Profile Data ---
    doc.setFont(undefined, 'bold');
    doc.text("Section 2: Profile Data", 14, currentY);
    
    autoTable(doc, {
      startY: currentY + 2,
      body: [
        ['Patient ID', id],
        ['Assessment Date', formatDate(user.createdDate)],
        ['Name', d?.Name || "N/A"],
        ['Age', d?.Age || "N/A"],
        ['Sex', d?.Sex || "N/A"],
        ['Height', `${d?.Height_cm || 0} cm`],
        ['Weight', `${d?.Weight_kg || 0} kg`],
        ['Family History Fatty Liver', d?.Family_History_Fatty_Liver || "No"],
        ['Family History Liver Disease', d?.Family_History_Liver_Disease || "No"]
      ],
      theme: 'striped',
      bodyStyles: { cellPadding: 1.5 },
      styles: { fontSize: 9 }
    });

    currentY = doc.lastAutoTable.finalY + 8;

    // --- Section 3: Remaining Variables ---
    doc.setFont(undefined, 'bold');
    doc.text("Section 3: Remaining Variables", 14, currentY);
    
    autoTable(doc, {
      startY: currentY + 2,
      body: [
        ['Smoking', d?.Smoking || "No"],
        ['Alcohol Days/Week', d?.Alcohol_Days_Per_Week || "0"],
        ['Coffee Habit', d?.Coffee_Habit || "No"],
        ['Coffee Cups/Day', d?.Coffee_Cups_Per_Day || "0"],
        ['Exercise', d?.Exercise || "No"],
        ['Sitting Hours/Day', d?.Sitting_Hours_Day || "N/A"],
        ['Stress Level', d?.Stress_Level || "Mild"],
        ['Meditation', d?.Meditation || "No"],
        ['Diet', d?.Diet || "Balanced"]
      ],
      theme: 'grid',
      bodyStyles: { cellPadding: 1.5 },
      styles: { fontSize: 9 }
    });

    // --- Page Break ---
    doc.addPage();
    currentY = 20;

    // --- Section 4: Interpretation ---
    doc.setFont(undefined, 'bold');
    doc.text("Section 4: Interpretation", 14, currentY);
    doc.setFont(undefined, 'normal');
    doc.setFontSize(10);
    const interpretationLines = [
      "This assessment estimates lifestyle and metabolic risk associated with fatty liver and related liver stress.",
      "It is intended for awareness and screening support, not as a standalone diagnosis.",
      "The strongest signals in this profile come from body measurements, metabolic burden, activity profile, and ultrasound status.",
      d?.Red_Flags ? `Flags identified: ${d.Red_Flags}` : "No major escalation flags were identified from the current inputs."
    ];
    doc.text(interpretationLines, 14, currentY + 7);

    // --- Section 5: Next Steps ---
    currentY += 40;
    doc.setFont(undefined, 'bold');
    doc.text("Section 5: Suggested Next Steps", 14, currentY);
    doc.setFont(undefined, 'normal');
    doc.text([
      "1. Reinforce healthy habits and maintain a stable weight and activity routine.",
      "2. Formal evaluation can be considered if symptoms, abnormal tests, or new risk factors appear."
    ], 14, currentY + 7);

    // --- Footer Disclaimer ---
    currentY += 30;
    doc.setDrawColor(200, 0, 0);
    doc.rect(12, currentY, pageWidth - 24, 40); 
    doc.setFont(undefined, 'bold');
    doc.setTextColor(200, 0, 0);
    doc.text("IMPORTANT DISCLAIMER", 14, currentY + 8);
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    const disclaimerText = "This assessment estimates lifestyle and metabolic risk associated with fatty liver and related liver stress. It is intended for awareness, screening support, and structured data collection. It does not establish a diagnosis of fatty liver disease or any other liver disorder by itself. Clinical interpretation should be done by a qualified doctor along with examination and appropriate investigations.";
    const splitDisclaimer = doc.splitTextToSize(disclaimerText, pageWidth - 30);
    doc.text(splitDisclaimer, 14, currentY + 15);

    doc.save(`LiverReport_${id}_${d?.Name}.pdf`);
  };

  const getRiskColor = (risk) => {
    const r = risk?.toLowerCase() || "";
    if (r.includes("high")) return "text-danger";
    if (r.includes("moderate")) return "text-warning";
    return "text-success";
  };

  if (!user) return <div className="p-5 text-center">Loading...</div>;

  const d = user.data;

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center gap-3">
          <button className="btn btn-light border rounded-circle" onClick={onBack} style={{ width: '40px', height: '40px' }}>
            <FaArrowLeft />
          </button>
          <h3 className="mb-0 fw-bold">Patient Details</h3>
        </div>
        <button className="btn btn-danger d-flex align-items-center gap-2 shadow-sm" onClick={generatePDF}>
          <FaFilePdf /> Export Assessment Report
        </button>
      </div>

      <div className="row g-4">
        {/* Patient Summary Header */}
        <div className="col-12">
          <div className="glass-card p-4 bg-white shadow-sm border-0">
            <div className="row text-center text-md-start">
              <div className="col-md-3 border-md-end mb-3 mb-md-0">
                <div className="d-flex align-items-center justify-content-center justify-content-md-start gap-3">
                  <div className="bg-primary bg-opacity-10 p-3 rounded-circle text-primary">
                    <FaUser size={24} />
                  </div>
                  <div>
                    <small className="text-muted text-uppercase fw-bold d-block" style={{ fontSize: '10px' }}>Patient Name</small>
                    <h5 className="mb-0 fw-bold">{d?.Name || "Anonymous"}</h5>
                  </div>
                </div>
              </div>
              <div className="col-md-3 border-md-end mb-3 mb-md-0">
                <small className="text-muted text-uppercase fw-bold d-block" style={{ fontSize: '10px' }}>Risk Level</small>
                <h5 className={`mb-0 fw-bold ${getRiskColor(d?.Risk_Level)}`}>{d?.Risk_Level || "Low"}</h5>
              </div>
              <div className="col-md-3 border-md-end mb-3 mb-md-0">
                <small className="text-muted text-uppercase fw-bold d-block" style={{ fontSize: '10px' }}>Fatty Liver Probability</small>
                <h5 className="mb-0 fw-bold text-primary">{d?.Fatty_Liver_Probability || 0}%</h5>
              </div>
              <div className="col-md-3">
                <small className="text-muted text-uppercase fw-bold d-block" style={{ fontSize: '10px' }}>Assessment Date</small>
                <h5 className="mb-0 fw-bold">{formatDate(user.createdDate)}</h5>
              </div>
            </div>
          </div>
        </div>

        {/* Column 1: Health Metrics */}
        <div className="col-md-7">
          <div className="glass-card p-4 bg-white shadow-sm border-0 h-100">
            <h6 className="fw-bold mb-4 d-flex align-items-center gap-2 border-bottom pb-2">
              <FaVial className="text-primary" /> Key Health Metrics
            </h6>
            <div className="row g-3">
              <div className="col-sm-4">
                <label className="text-muted small d-block">BMI Score</label>
                <span className="fw-bold">{d?.BMI || "N/A"}</span>
                <div className="text-muted" style={{ fontSize: '11px' }}>{d?.BMI_Category}</div>
              </div>
              <div className="col-sm-4">
                <label className="text-muted small d-block">Fibrosis Concern</label>
                <span className="fw-bold">{d?.Fibrosis_Concern_Probability || 0}%</span>
              </div>
              <div className="col-sm-4">
                <label className="text-muted small d-block">Waist (cm)</label>
                <span className="fw-bold">{d?.Waist_cm || 0}</span>
                <div className="text-muted" style={{ fontSize: '11px' }}>{d?.Waist_Risk}</div>
              </div>
              <div className="col-sm-4">
                <label className="text-muted small d-block">Raw Score</label>
                <span className="fw-bold">{d?.Raw_Score || 0}/46</span>
              </div>
              <div className="col-sm-4">
                <label className="text-muted small d-block">Weekly Alcohol</label>
                <span className="fw-bold">{d?.Weekly_Alcohol_Intake || 0} units</span>
              </div>
              <div className="col-sm-4">
                <label className="text-muted small d-block">Sitting Hours</label>
                <span className="fw-bold">{d?.Sitting_Hours_Day || "N/A"} / day</span>
              </div>
            </div>

            <div className="mt-4 p-3 rounded bg-light">
              <h6 className="small fw-bold text-danger mb-2">
                <FaExclamationTriangle className="me-1"/> Red Flags
              </h6>
              <p className="small mb-0 text-muted">{d?.Red_Flags || "No major escalation flags identified."}</p>
            </div>
          </div>
        </div>

        {/* Column 2: Lifestyle */}
        <div className="col-md-5">
          <div className="glass-card p-4 bg-white shadow-sm border-0 h-100">
            <h6 className="fw-bold mb-4 d-flex align-items-center gap-2 border-bottom pb-2">
              <FaHistory className="text-primary" /> Lifestyle & Habits
            </h6>
            <div className="mb-3">
              <label className="text-muted small d-block">Diet & Beverage</label>
              <span className="fw-bold">{d?.Diet || "N/A"}</span>
              <div className="d-flex align-items-center gap-2 mt-1">
                <FaCoffee className="text-secondary" />
                <small className="text-muted">
                    Coffee: {d?.Coffee_Habit === "Yes" ? `${d?.Coffee_Cups_Per_Day} cups/day` : "No habit"}
                </small>
              </div>
            </div>
            <div className="mb-3">
              <label className="text-muted small d-block">Exercise Habits</label>
              <span className="fw-bold">{d?.Exercise === "Yes" ? `${d?.Exercise_Days_Week} days/week` : "No regular exercise"}</span>
              <p className="text-muted small mb-0">{d?.Exercise_Duration}</p>
            </div>
            <div className="mb-3">
              <label className="text-muted small d-block">Stress & Meditation</label>
              <span className="fw-bold">{d?.Stress_Level || "None"} Stress</span>
              <p className="text-muted small mb-0">Meditation: {d?.Meditation || "No"}</p>
            </div>
            <div>
              <label className="text-muted small d-block">Ultrasound (USG)</label>
              <span className="fw-bold">{d?.USG_Fatty_Liver === "Yes" ? `Grade ${d?.USG_Grade}` : "Not done / No Fatty Liver"}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}